// 影像牆的一格：SDK LiveView（WebRTC）→ <video>。session 建立不等於已連上，收到第一個影格才顯示畫面（VMS §4）。
// 中斷自動重連（LIVE-04，Eddy 2026-09-27；reconnect.ts）：可恢復的錯誤 / 影像停住 → 指數退避 2 → 60 秒、格子顯示「重連中（第 n 次）」；
// 權限 / 席次 / 授權類錯誤、重送也不會變的設備拒絕（codec_unsupported / invalid_payload）不重試，維持失敗畫面（手動「重試」鈕一直都在）。
// codec_unsupported 的文案請使用者把這個通道的子碼流改成 H.264；平台目前沒有通道編碼的設定欄位（kinio_channel.codec 唯讀、設備未回報），
// 所以只給文案、不附設定連結（vms-web H.265 段，2026-09-28）。
// LIVE-08：數位放大（滾輪 / 拖曳 / 雙擊還原，zoom.ts）、快照下載（有 recording.export 才出現；瀏覽器端擷取目前影格，不經伺服器）、
// 倒退 30 秒回看（切到回放頁、同一台、現在 − 30 秒）。LIVE-04 格子狀態：路徑 / codec / 解析度 / fps / 位元率（stats.ts）。
// LIVE-02 手動：碼流標籤可點，主 / 子碼流切換（覆蓋自動選擇，這一格有效）。
// 多鏡頭（plan SPEC #multi-lens-live；生效能力有 kinio_lens、shadow 宣告 ≥ 2 顆）：格子標頭多一組鏡頭單選（radiogroup），
// 同一條連線 ls-ctrl `select {lens}` 換鏡頭；設備 state.lens_active 等於所選而且之後第一個影格到才算切好（之前的影格不算），
// 冷 6 秒 / 熱 4 秒逾時 →「下鏡頭沒有回應」〔重試〕〔看上鏡頭〕；hello 沒有 caps.lens（舊韌體）→ 鏡頭單選停用。
// 隱私遮罩把看副鏡頭的切回主鏡頭 → 提示。重連回到斷線前的鏡頭；快照檔名帶鏡頭名、回看帶 lens。VMS 第一版不做雙畫面。
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  LiveView,
  liveOutcomeOf,
  livePhaseGroup,
  onFirstFrame,
  reportLiveFirstFrame,
  reportLiveOutcome,
  vms,
  vmsReserve,
  type LensPhase,
  type LiveMediaPath,
  type LivePhase,
  type LiveStopReason,
  type LiveTtffMeta,
  type TalkState,
} from '@kinio/web-sdk';
import { lensName, useLensDeclState } from '../../lib/lens';
import type { Presence, VmsDevice } from '../../lib/devices';
import { camDot, camState, reportLiveOffline, reportTileState } from '../../lib/liveStatus';
import { PtzPanel } from './PtzPanel';
import { DevicePtzPanel } from './DevicePtzPanel';
import { CONNECT_TIMEOUT_MS, STALL_MS, backoffMs, isRetryable, leaveStopReason, liveErrorKeys, retryStopReason } from './reconnect';
import { formatBitrate, parseStats, type StatsSample, type TileStats } from './stats';
import { isRelayStop, lastActivity, markActivity, relayVerdict } from './relayGuard';
import { ZOOM_IDENTITY, panZoom, wheelZoom, type Zoom } from './zoom';
import { PreviewSnapshot } from './PreviewSnapshot';
import { mayBeAsleep } from '../../lib/alarmPopup';
import { playbackHref } from '../../lib/links';
import { stamp } from '../../lib/timefmt';

interface Props {
  /** 通道的 parent（NVR / Kinio Station）在線狀態：分「通道離線」與「設備離線」（第二輪 B2-20） */
  parentPresence?: Presence;
  cam: VmsDevice;
  label: string;
  /** 滑過名稱顯示的完整路徑（「lab › NVR631 / CH4」，P1-2：名稱不截斷成「NVR631/…」） */
  path?: string;
  /** 目前選取的格子（點格子選取；樹上點攝影機時沒有空格就取代這一格，LIVE-01 格位規則） */
  selected?: boolean;
  onSelect?: () => void;
  /** 多格同時開時錯開啟動，避免同一台 NVR 同時收到多個 offer。 */
  startDelayMs?: number;
  /** 這格收掉時帶給平台的停止原因（stop_viewing.reason）：影像牆 multiview_leave，其他（設備頁、地圖、警報）預設 user_leave。 */
  leaveReason?: LiveStopReason;
  onClose?: () => void;
  onExpand?: () => void;
  /** 碼流（LIVE-02）：小格子子碼流、大格 / 放大主碼流。 */
  stream?: 'main' | 'sub';
  /** 解碼預算用完（LIVE-03）：不開即時，顯示預覽，點「轉即時」才解碼。 */
  preview?: boolean;
  onPromote?: () => void;
  /**
   * 這一格可以開 PTZ 控制盤（LIVE-06）：呼叫端已依能力確認（lib/caps.ts controlCaps：獨立設備生效能力有 kinio_ptz、
   * 通道 kinio_channel.abilities.ptz）。通道走 VMS PTZ façade（PtzPanel）、獨立設備走 kinio_ptz.jog（DevicePtzPanel）；
   * 萬一還是回不支援（422），控制盤自己收起、按鈕不再出現。
   */
  ptz?: boolean;
  /**
   * 這一格能對講（LIVE-07）：呼叫端已確認是放大 / 單一畫面、設備有 kinio_audio、使用者在這個家戶有 media.talk。
   * 開了才協商聲音與對講（audio sendrecv + ls-ctrl）；伺服器另外驗 media.talk（沒有 → 按鈕停用並說明）。
   */
  talk?: boolean;
  /** 可以下載快照（這個家戶有 recording.export；LIVE-08）。 */
  canSnapshot?: boolean;
  /** 牆上拖放（LIVE-01）：底列可拖（帶格位），整格可放。放大那一格不給。 */
  dnd?: {
    cell: number;
    over: boolean;
    dataType: string;
    onDragOver(e: React.DragEvent): void;
    onDragLeave(): void;
    onDrop(e: React.DragEvent): void;
  };
  style?: CSSProperties;
  /** 這一格不在目前範圍：角標寫它在哪（「EddyStudio › lab」；第五輪 B5-07） */
  outOfScope?: string;
  /** 放大那一格：設備的〔⋯〕（警報器等）放進底部控制列（第五輪 R5-P2-4：不再擠在頁首、緊靠〔回影像牆〕） */
  extraActions?: React.ReactNode;
}

/** 快照檔名：名稱 + 本地時間（檔名不能有的字元換成 _）。 */
export function snapshotName(label: string, at: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const ts = `${at.getFullYear()}${p(at.getMonth() + 1)}${p(at.getDate())}-${p(at.getHours())}${p(at.getMinutes())}${p(at.getSeconds())}`;
  return `${label.replace(/[\\/:*?"<>|\s]+/g, '_')}-${ts}.png`;
}

/** 目前影格 → PNG 下載（原始解析度，不含放大）。 */
function downloadSnapshot(v: HTMLVideoElement, name: string): boolean {
  if (!v.videoWidth || !v.videoHeight) return false;
  const c = document.createElement('canvas');
  c.width = v.videoWidth;
  c.height = v.videoHeight;
  const ctx = c.getContext('2d');
  if (!ctx) return false;
  ctx.drawImage(v, 0, 0, c.width, c.height);
  c.toBlob((b) => {
    if (!b) return;
    const url = URL.createObjectURL(b);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }, 'image/png');
  return true;
}

/**
 * 預置點縮圖：目前影格縮成 320×180 JPEG（品質 0.7、≤ 32 KB，SPEC #multi-lens-ptz）；雲台停 1 秒後再截。沒有畫面 / 太大 = null。
 */
export function captureThumbJpeg(v: HTMLVideoElement, settleMs = 1000): Promise<Blob | null> {
  return new Promise((resolve) => {
    setTimeout(() => {
      if (!v.videoWidth || !v.videoHeight) return resolve(null);
      const c = document.createElement('canvas');
      c.width = 320;
      c.height = 180;
      const ctx = c.getContext('2d');
      if (!ctx) return resolve(null);
      ctx.drawImage(v, 0, 0, c.width, c.height);
      c.toBlob((b) => resolve(b && b.size <= 32 * 1024 ? b : null), 'image/jpeg', 0.7);
    }, settleMs);
  });
}

/** 開始連線後多久還沒有第一個影格就提示並給〔重試〕：還在建連線寫「連線比平常久…」，已連上寫等第一個畫面 / 休眠
 *  （第十一輪 B11-06：Wi-Fi 攝影機「連線中…」30–50 秒文字不變；原本只有已連上 12 秒才提示）。 */
export const SLOW_FIRST_FRAME_MS = 10_000;

/** 格子標頭依格寬（VMS UX 評審第三輪 R3-P0-1、B3-01；SPEC LIVE-01「格子標頭依格寬」）：
 *  < COMPACT：名稱＋〔⤢〕〔⋯〕〔✕〕；< WIDE：再多碼流；其餘收進〔⋯〕。≥ WIDE 全部攤開。〔✕〕〔⤢〕永遠看得到。 */
export const COMPACT_TILE_PX = 320;
export const WIDE_TILE_PX = 520;
export type TileWidthMode = 'compact' | 'mid' | 'wide';
export function tileWidthMode(w: number): TileWidthMode {
  return w < COMPACT_TILE_PX ? 'compact' : w < WIDE_TILE_PX ? 'mid' : 'wide';
}

/** 觀看端首幀遙測（SPEC #media-live-ttff）：VMS 網頁一定帶 client = vms（平台從 SDK 標頭分不出 VMS 與 LS Home 網頁）。 */
const TTFF: LiveTtffMeta = { client: 'vms', appVersion: typeof __WEB_BUILD__ === 'string' ? __WEB_BUILD__ : undefined };

export function LiveTile({
  cam,
  label,
  path,
  selected,
  onSelect,
  startDelayMs = 0,
  leaveReason = 'user_leave',
  onClose,
  onExpand,
  stream: autoStream,
  preview,
  onPromote,
  ptz,
  talk,
  canSnapshot,
  dnd,
  style,
  parentPresence,
  outOfScope,
  extraActions,
}: Props) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);
  // 標頭依格寬（R3-P0-1）：compact = 名稱＋⤢ ⋯ ✕；mid 多碼流；wide 全部攤開
  const [mode, setMode] = useState<TileWidthMode>('wide');
  const narrow = mode !== 'wide';
  // 連線方式（中繼 / 區網、codec、解析度）不常駐，按 ⓘ 才顯示（R2-P0-2 ④）
  const [statsOpen, setStatsOpen] = useState(false);
  useEffect(() => {
    const el = tileRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setMode(tileWidthMode(el.getBoundingClientRect().width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const lvRef = useRef<LiveView | null>(null);
  /** LIVE-02 手動覆蓋（null = 照自動）。 */
  const [streamOverride, setStreamOverride] = useState<'main' | 'sub' | null>(null);
  const stream = streamOverride ?? autoStream;
  const [zoom, setZoom] = useState<Zoom>(ZOOM_IDENTITY);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const [stats, setStats] = useState<TileStats | null>(null);
  /** 這一次連線（nonce）的遠端 stream；只有它的 loadeddata 算第一個影格。 */
  const streamRef = useRef<MediaStream | null>(null);
  const [phase, setPhase] = useState<LivePhase>('idle');
  const [err, setErr] = useState<string | null>(null);
  const [errStatus, setErrStatus] = useState(0);
  const [dlgNote, setDlgNote] = useState<{ reason: string; from: string } | null>(null);
  /** 連續第幾次自動重連（0 = 沒在重連；收到第一個影格歸零）。 */
  const [attempt, setAttempt] = useState(0);
  /** 下一次自動重連的時間（ms epoch；null = 沒排）。 */
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const [, setTick] = useState(0);
  const [firstFrame, setFirstFrame] = useState(false);
  // 錯開啟動的延遲只在第一次連線用：切版型、換頁時格位編號變了不重新連線（LIVE-01 格位規則、第二輪 R2-P0-2 ③）
  const startDelayRef = useRef(startDelayMs);
  // 串流回的離線碼告訴左樹（同一個狀態來源，B2-20）；有畫面或格子拿掉就清掉
  useEffect(() => {
    // 獨立攝影機的 presence 是在線時不回報離線（左樹跟總覽一樣是在線，第十輪 B10-08）
    const off = !firstFrame && (err === 'CHANNEL_OFFLINE' || (err === 'DEVICE_OFFLINE' && (!!cam.parentAid || cam.presence !== 'online')));
    reportLiveOffline(cam.key, off);
  }, [cam.key, cam.parentAid, cam.presence, err, firstFrame]);
  useEffect(() => () => reportLiveOffline(cam.key, false), [cam.key]);
  const [noVideo, setNoVideo] = useState(false);
  const [nonce, setNonce] = useState(0);
  // 這一次連線第一個影格畫上去的時間（LiveView.now() 時鐘；null = 還沒有）與目前量到的路徑：兩個都有才送 media-ready
  const firstFrameAt = useRef<number | null>(null);
  const pathRef = useRef<LiveMediaPath | null>(null);
  const reportFirstFrame = useCallback(() => {
    const lv = lvRef.current;
    if (lv && firstFrameAt.current !== null) void reportLiveFirstFrame(lv, pathRef.current, firstFrameAt.current, TTFF);
  }, []);
  // 下一次收掉這一路的停止原因（重連前設；用一次就清）；cleanup 時 streamNow 已經是新的碼流（換碼流 = stream_switch）
  const stopReasonRef = useRef<LiveStopReason | null>(null);
  const streamNow = useRef(stream);
  streamNow.current = stream;
  const leaveReasonRef = useRef(leaveReason);
  leaveReasonRef.current = leaveReason;
  const [slow, setSlow] = useState(false);
  const [ptzOpen, setPtzOpen] = useState(false);
  const [ptzNone, setPtzNone] = useState(false);
  const wantTalk = !!talk && !preview;
  const [talkState, setTalkState] = useState<TalkState>('off');
  /** 伺服器 / 設備說不能對講的原因（連上後才知道）。 */
  const [talkBlock, setTalkBlock] = useState<'forbidden' | 'incapable' | null>(null);
  const [listen, setListen] = useState(false);
  const canPtz = !!ptz && cam.presence !== 'offline' && !ptzNone;
  // 多鏡頭：宣告（shadow kinio_lens）、目前在看的鏡頭（重連帶它）、切換狀態、hello caps.lens（undefined = 還沒 hello；null = 舊韌體）
  const { decl, ready: lensReady } = useLensDeclState(cam);
  const multi = !!decl && !preview;
  const lensRef = useRef(0);
  const [lens, setLens] = useState(0);
  const [lensPhase, setLensPhase] = useState<LensPhase | null>(null);
  const [lensCaps, setLensCaps] = useState<number[] | null | undefined>(undefined);
  const [lensNote, setLensNote] = useState<'moved_back' | null>(null);
  const lname = (id: number) => lensName(t, decl, id);

  useEffect(() => {
    if (cam.presence === 'offline' || preview || !lensReady) return; // 多鏡頭機種等宣告讀完再連（不重開，lib/lens useLensDeclState）
    setPhase('idle'); // 上一次的 failed 不帶進這一次（錯開啟動最多 6 秒，這段時間是「連線中」不是失敗）
    setFirstFrame(false);
    setErr(null);
    setErrStatus(0);
    setRetryAt(null);
    setNoVideo(false);
    setSlow(false);
    setTalkState('off');
    setTalkBlock(null);
    setLensPhase(null);
    setLensCaps(undefined);
    // 上一次的 stream 還掛在 <video> 上（留著最後一格畫面）：它的軌道被關掉時 Chrome 會再發一次 loadeddata，
    // 不能當成這一次的第一個影格（否則次數被歸零、連線逾時與停住偵測都判斷錯；mock live-reconnect 重現過）
    streamRef.current = null;
    firstFrameAt.current = null;
    pathRef.current = null;
    let offFrame: (() => void) | null = null;
    const lv = new LiveView({
      apiEndpoint: cam.apiEndpoint,
      homeId: cam.homeId,
      deviceRef: cam.aid,
      audio: wantTalk,
      talk: wantTalk,
      onTalk: setTalkState,
      stream,
      ...(multi
        ? {
            lens: lensRef.current,
            lensControl: true,
            onLensHello: (l: number[] | null) => setLensCaps(l),
            onLensPhase: (p: LensPhase) => {
              setLensPhase(p);
              if (p.kind === 'showing') {
                lensRef.current = p.lens;
                setLens(p.lens);
              }
            },
            onLensState: (st: { lensActive: number | null; blocked: Record<number, string> }) => {
              // 隱私遮罩把看副鏡頭的主連線切回 lens 0（設備自己切的）
              if (st.lensActive === 0 && lensRef.current !== 0 && Object.values(st.blocked).includes('privacy_mask')) setLensNote('moved_back');
            },
          }
        : {}),
      // VMS façade（plan/vms-system-v0.md §5.1）：先預留 live-session 拿一次性 ticket，再 start_viewing 帶上
      reserve: vmsReserve(cam.apiEndpoint, cam.homeId, cam.aid, stream),
      onPhase: (p) => {
        setPhase(p);
        if (p === 'connected' && !lv.hasRemoteVideo) setNoVideo(true);
        if (p === 'connected' && wantTalk) setTalkBlock(lv.talkAllowed === false ? 'forbidden' : null);
      },
      onStream: (s) => {
        const v = videoRef.current;
        if (v && s) {
          streamRef.current = s;
          v.srcObject = s;
          offFrame?.();
          offFrame = onFirstFrame(v, () => {
            if (lvRef.current !== lv || firstFrameAt.current !== null) return;
            firstFrameAt.current = lv.now();
            reportFirstFrame();
          });
          void v.play().catch(() => {});
        }
      },
      onError: (e) => {
        setErrStatus(e.status ?? 0);
        setErr(e.code || e.message);
        // 委派結束 / 縮小、委派方要求兩步驟驗證（SPEC #org-tree-ui 第 1 點 ux-2-5 / ux-2-8）：寫出委派方，不只是「權限已更新」
        const d = (e as { detail?: Record<string, unknown> }).detail;
        const reason = typeof d?.reason === 'string' ? d.reason : '';
        setDlgNote(['delegation_ended', 'delegation_changed', 'delegator_mfa_required'].includes(reason) ? { reason, from: String(d?.from_org_name ?? '') } : null);
      },
    });
    lvRef.current = lv;
    const startTimer = setTimeout(() => void lv.start(), startDelayRef.current);
    const slowTimer = setTimeout(() => setSlow(true), startDelayRef.current + SLOW_FIRST_FRAME_MS);
    return () => {
      clearTimeout(startTimer);
      clearTimeout(slowTimer);
      offFrame?.();
      const sid = lv.reservedSessionId;
      const reason = stopReasonRef.current ?? leaveStopReason(stream, streamNow.current, leaveReasonRef.current);
      // 沒出圖就收掉（離開 / 換碼流 / 重連）：這一次觀看還沒報過才送（要在 dispose 前，候選型別才量得到）
      if (firstFrameAt.current === null) void reportLiveOutcome(lv, liveOutcomeOf({ stopReason: reason }), TTFF);
      lv.dispose(reason);
      stopReasonRef.current = null;
      if (lvRef.current === lv) lvRef.current = null;
      if (sid) void vms.endSession(cam.apiEndpoint, cam.homeId, sid).catch(() => undefined); // 冪等收尾（設備停送流）
    };
  }, [cam.aid, cam.homeId, cam.apiEndpoint, cam.presence, nonce, stream, preview, wantTalk, multi, lensReady, reportFirstFrame]);

  // 沒出圖的結果（SPEC #media-live-ttff）：錯誤 / 連線逾時 / 連上但沒有影像軌——這一次觀看只報一筆（SDK 擋重複）
  useEffect(() => {
    const lv = lvRef.current;
    if (!lv || preview || firstFrameAt.current !== null || (!err && !noVideo)) return;
    void reportLiveOutcome(lv, err ? liveOutcomeOf({ code: err }) : 'no_video', TTFF);
  }, [err, noVideo, preview]);

  // 多鏡頭切換中：每個解碼後的影格告訴 SDK（lens_active 之後的第一格才算切好）；沒有 requestVideoFrameCallback 就每 100 ms 看一次
  const lensWaiting = !!lensPhase && lensPhase.kind !== 'showing';
  useEffect(() => {
    const v = videoRef.current as (HTMLVideoElement & { requestVideoFrameCallback?(cb: () => void): number; cancelVideoFrameCallback?(h: number): void }) | null;
    const lv = lvRef.current;
    if (!lensWaiting || !v || !lv) return;
    let alive = true;
    if (v.requestVideoFrameCallback) {
      let h = 0;
      const step = () => {
        if (!alive) return;
        lv.lensFrame();
        h = v.requestVideoFrameCallback!(step);
      };
      h = v.requestVideoFrameCallback(step);
      return () => {
        alive = false;
        v.cancelVideoFrameCallback?.(h);
      };
    }
    const iv = setInterval(() => lv.lensFrame(), 100);
    return () => clearInterval(iv);
  }, [lensWaiting, nonce]);
  useEffect(() => {
    if (!lensNote) return;
    const timer = setTimeout(() => setLensNote(null), 8_000);
    return () => clearTimeout(timer);
  }, [lensNote]);
  /** 使用者選了鏡頭 id（舊韌體 / 還沒連上 = 不送）。 */
  const pickLens = (id: number) => {
    const lv = lvRef.current;
    if (!lv || !lensCaps?.includes(id)) return;
    setLensNote(null);
    lv.selectLens(id);
  };
  const lensTarget = lensPhase?.kind === 'switching' ? lensPhase.to : lensPhase?.kind === 'failed' ? lensPhase.lens : lens;

  // 收到影格 = 恢復：次數歸零
  useEffect(() => {
    if (firstFrame) setAttempt(0);
  }, [firstFrame]);

  // 可恢復的錯誤 → 排下一次重連（指數退避）；授權類不排
  const retryable = !!err && isRetryable(err, errStatus);
  useEffect(() => {
    if (!err || !retryable || preview || cam.presence === 'offline') return;
    const n = attempt + 1;
    const wait = backoffMs(n);
    setRetryAt(Date.now() + wait);
    const timer = setTimeout(() => {
      setAttempt(n);
      stopReasonRef.current = retryStopReason(err, false);
      setNonce((x) => x + 1);
    }, wait);
    const tick = setInterval(() => setTick((x) => x + 1), 1000); // 倒數秒數
    return () => {
      clearTimeout(timer);
      clearInterval(tick);
    };
    // attempt 只在排程當下讀（不列進 deps：否則 setAttempt 會把剛排好的重連清掉）；恢復時由上面的 effect 歸零
  }, [err, retryable, preview, cam.presence]);

  // 這一次連線（nonce）開始後 CONNECT_TIMEOUT_MS 還沒有第一個影格、也沒有錯誤 → 當成失敗，照樣排下一次重連
  // （SDK 某一步沒有逾時就會永遠停在「連線中 / 重新連線中」：W17 第二輪 2026-09-27 not_recovered）。
  // 已連上但對方沒有影像軌（noVideo）是另一種畫面，不算。
  useEffect(() => {
    if (firstFrame || err || preview || noVideo || cam.presence === 'offline') return;
    const timer = setTimeout(() => {
      setErrStatus(0);
      setErr('CONNECT_TIMEOUT');
    }, startDelayRef.current + CONNECT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [firstFrame, err, preview, noVideo, cam.presence, nonce]);

  // 已經在播、影像停住（currentTime 不前進）超過 STALL_MS → 當成中斷重連（設備重連 / gateway 重啟時 pc 不一定馬上 failed）
  useEffect(() => {
    if (!firstFrame || err || preview) return;
    let last = -1;
    let since = Date.now();
    const iv = setInterval(() => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden) {
        since = Date.now(); // 背景分頁可能不推進播放時間：不算停住
        return;
      }
      if (v.currentTime !== last) {
        last = v.currentTime;
        since = Date.now();
      } else if (Date.now() - since >= STALL_MS) {
        setErrStatus(0);
        setErr('STALLED');
      }
    }, 2_000);
    return () => clearInterval(iv);
  }, [firstFrame, err, preview, nonce]);

  // LIVE-04 格子狀態：有畫面時每 2 秒取一次 getStats
  useEffect(() => {
    if (!firstFrame || err || preview) {
      setStats(null);
      return;
    }
    let prev: StatsSample | null = null;
    let alive = true;
    const poll = async () => {
      const pc = lvRef.current?.peerConnection;
      if (!pc) return;
      try {
        const r = parseStats(await pc.getStats(), prev);
        prev = r.sample;
        if (alive) setStats(r.stats);
      } catch {
        /* pc 已關 */
      }
    };
    void poll();
    const iv = setInterval(() => void poll(), 2_000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [firstFrame, err, preview, nonce]);

  // AUDIT-03 + SPEC #media-live-ttff：這一次連線第一次知道路徑、也有第一個影格就送 media-ready（路徑稽核 + 首幀計時）；失敗不影響觀看
  useEffect(() => {
    if (!stats?.path) return;
    pathRef.current = stats.path;
    reportFirstFrame();
  }, [stats?.path, nonce, reportFirstFrame]);

  // LIVE-12 中繼節流：走 relay 時閒置 5 分鐘提示、再 30 秒斷線；單次 30 分鐘到期斷線。斷線後按「繼續觀看」重新連
  const [relayWarn, setRelayWarn] = useState<number | null>(null);
  const onRelay = stats?.path === 'relay';
  useEffect(() => {
    if (!onRelay || err || preview) {
      setRelayWarn(null);
      return;
    }
    const since = Date.now();
    const iv = setInterval(() => {
      const v = relayVerdict('relay', since, lastActivity(), Date.now());
      if (v.kind === 'stop') {
        setRelayWarn(null);
        const lv = lvRef.current;
        const sid = lv?.reservedSessionId;
        lv?.dispose(); // 真的停掉串流（不是只蓋一層）
        if (sid) void vms.endSession(cam.apiEndpoint, cam.homeId, sid).catch(() => undefined);
        setErrStatus(0);
        setErr(v.reason);
      } else setRelayWarn(v.kind === 'idle_warn' ? Math.ceil(v.leftMs / 1000) : null);
    }, 1000);
    return () => clearInterval(iv);
  }, [onRelay, err, preview, nonce, cam.apiEndpoint, cam.homeId]);

  // LIVE-08 數位放大：滾輪要能 preventDefault（React 的 onWheel 是 passive），所以直接掛原生事件
  useEffect(() => {
    const el = tileRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!firstFrame || (e.target as HTMLElement).closest('.tile-bar, .ptz-panel, .tile-overlay button')) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      setZoom(wheelZoom(zoomRef.current, e.deltaY, e.clientX - r.left, e.clientY - r.top, r.width, r.height));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [firstFrame]);
  const drag = useRef<{ x: number; y: number } | null>(null);

  const group = cam.presence === 'offline' ? 'offline' : livePhaseGroup(phase);
  // 格子與左樹同一個狀態函式（lib/liveStatus）：parent 在線但通道離線寫「通道離線」，不寫「設備離線」
  const state = camState(cam, parentPresence, err);
  // 左樹讀同一份（B3-07：切範圍後這格不在範圍的設備清單裡，樹也用格子算出的狀態）；放大畫面不登記（同一台牆上那格已登記）
  const tileKey = onClose ? cam.key : '';
  useEffect(() => {
    if (tileKey) reportTileState(tileKey, state);
  }, [tileKey, state]);
  useEffect(() => () => (tileKey ? reportTileState(tileKey, null) : undefined), [tileKey]);
  // 串流說設備離線、但設備狀態是在線（獨立攝影機）：寫「連不上畫面」，不寫「設備離線」（第十輪 B10-08）
  const errShown = err === 'DEVICE_OFFLINE' && state === 'channel_offline' ? 'CHANNEL_OFFLINE' : err === 'DEVICE_OFFLINE' && state === 'online' ? 'UNREACHABLE' : err;
  let overlay: string | null = null;
  let overlaySub: string | null = null;
  if (cam.presence === 'offline') overlay = state === 'channel_offline' ? t('live.err_CHANNEL_OFFLINE') : t('live.offline');
  else if (preview) overlay = t('live.preview');
  else if (err && retryable && retryAt) {
    overlay = t(liveErrorKeys(errShown!), { defaultValue: t('live.failed_retrying') });
    overlaySub = t('live.reconnect_wait', { s: Math.max(0, Math.ceil((retryAt - Date.now()) / 1000)), n: attempt + 1 });
  } else if (err && dlgNote)
    overlay =
      dlgNote.reason === 'delegation_ended'
        ? t('ot.live_dlg_ended', { from: dlgNote.from, time: stamp(Date.now()) })
        : dlgNote.reason === 'delegation_changed'
          ? t('ot.live_dlg_changed', { from: dlgNote.from })
          : t('ot.mfa_needed', { from: dlgNote.from });
  // 字串表沒有的碼不露在畫面上（代碼放滑過的提示）：會自動重連的寫「連線中斷，正在重連…」，其他寫「無法觀看」（第十二輪 B12-06）
  else if (err) overlay = t(liveErrorKeys(errShown!), { defaultValue: t(retryable ? 'live.failed_retrying' : 'live.failed_plain') });
  else if (noVideo) overlay = t('live.no_video');
  else if (!firstFrame && attempt > 0) overlay = t('live.reconnecting', { n: attempt });
  // 休眠文案只給電池 / HaLow（第三輪 B3-03）；NVR 通道與插電攝影機寫「等第一個畫面」
  else if (!firstFrame) overlay = !slow ? t('live.connecting') : phase === 'connected' ? t(mayBeAsleep(cam) ? 'live.slow_first_frame' : 'live.slow_first_frame_wired') : t('live.slow_connect');
  // 連線比平常久（還沒有第一個畫面、沒有錯誤）：給〔重試〕重新連這一格；45 秒逾時自動重連照舊
  const slowRetry = slow && !firstFrame && !err && !noVideo && !preview && cam.presence !== 'offline';
  // 會自動重連的錯誤：前兩次（等 2、4 秒）不放〔重試〕，第 3 次起才放〔立刻重連〕；不會自動重連的照舊〔重試〕（第十二輪 B12-06）
  const autoRetry = !!err && retryable && !preview && cam.presence !== 'offline';
  const retryBtn = autoRetry ? attempt >= 2 : !!err || group === 'failed' || slowRetry;

  /** 按住說話：按下開、放開關。連上後設備的 hello 說沒有對講 → 停用。 */
  const talkHold = (on: boolean) => {
    const lv = lvRef.current;
    if (!lv || talkBlock) return;
    if (on && lv.talkCapable === false) {
      setTalkBlock('incapable');
      return;
    }
    if (!on && lv.talk === 'off') return;
    void lv.setTalk(on).then(() => {
      if (lv.talk === 'forbidden') setTalkBlock('forbidden');
    });
  };
  const talkMsg = talkBlock ?? (talkState === 'busy' || talkState === 'denied' || talkState === 'error' || talkState === 'unavailable' ? talkState : null);

  // 狀態點跟格子內容一致（P1-2、B-17）：格子回通道 / 設備離線就是紅的，不只看 parent 的在線狀態
  const dotState = camDot(state);
  // 多鏡頭：快照檔名帶鏡頭名（<設備名>_<鏡頭名>-時間，SPEC #multi-lens-capture）；回看帶 lens
  const snapshot = () => videoRef.current && downloadSnapshot(videoRef.current, snapshotName(multi ? `${label}_${lname(lens)}` : label, new Date()));
  // 回放頁從 at − 5 秒開始播（事件前情），所以帶 now − 25 → 從 30 秒前開始
  const replay = () => nav(playbackHref(cam.key, Math.floor(Date.now() / 1000) - 25, multi && lens ? { lens } : {}));
  const lensMsg =
    lensPhase?.kind === 'switching'
      ? { warn: false, text: t('lens.switching', { name: lname(lensPhase.to) }) }
      : lensPhase?.kind === 'failed'
        ? {
            warn: true,
            text: t(lensPhase.err === 'TIMEOUT' ? 'lens.timeout' : lensPhase.reason === 'privacy_mask' ? 'lens.privacy' : 'lens.unsupported', { name: lname(lensPhase.lens) }),
            retry: lensPhase.err === 'TIMEOUT' ? lensPhase.lens : null,
            back: lensPhase.showing,
          }
        : lensNote === 'moved_back'
          ? { warn: true, text: t('lens.moved_back', { name: lname(0) }) }
          : null;
  return (
    <div
      ref={tileRef}
      className={`tile tile-${preview ? 'preview' : err && retryable ? 'reconnecting' : group}${zoom.s > 1 ? ' tile-zoomed' : ''}${dnd?.over ? ' drop-target' : ''}${selected ? ' tile-selected' : ''}${dotState === 'offline' ? ' tile-offline' : ''}${narrow ? ' tile-narrow' : ''} tile-w-${mode}${statsOpen ? ' stats-open' : ''}`}
      style={style}
      onPointerDownCapture={onSelect}
      aria-current={selected || undefined}
      data-cell={dnd?.cell}
      onDragOver={dnd?.onDragOver}
      onDragLeave={dnd?.onDragLeave}
      onDrop={dnd?.onDrop}
      data-stream={stream}
      data-zoom={zoom.s > 1 ? zoom.s.toFixed(2) : undefined}
      data-path={stats?.path ?? undefined}
      data-reconnect={attempt || undefined}
      data-error={err ?? undefined}
      data-phase={phase}
    >
      <video
        ref={videoRef}
        muted={!(wantTalk && listen)}
        playsInline
        autoPlay
        onLoadedData={(e) => {
          if (streamRef.current && e.currentTarget.srcObject === streamRef.current) setFirstFrame(true);
        }}
        onDoubleClick={() => setZoom(ZOOM_IDENTITY)}
        onPointerDown={(e) => {
          if (zoom.s === 1) return;
          drag.current = { x: e.clientX, y: e.clientY };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          const r = tileRef.current?.getBoundingClientRect();
          if (!d || !r) return;
          setZoom((z) => panZoom(z, e.clientX - d.x, e.clientY - d.y, r.width, r.height));
          drag.current = { x: e.clientX, y: e.clientY };
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        style={zoom.s > 1 ? { transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.s})`, transformOrigin: '0 0' } : undefined}
        title={firstFrame ? t('live.zoom_hint') : undefined}
        aria-label={label}
      />
      {/* LIVE-03：超過解碼預算的預覽格顯示這台最新的事件快照、每 5 秒更新（不解碼影像） */}
      {preview && cam.presence !== 'offline' && <PreviewSnapshot cam={cam} />}
      {stats && (
        <div className="tile-stats small" data-testid="tile-stats">
          {[
            stats.path && t(`live.path_${stats.path}`),
            stats.codec,
            stats.width && stats.height ? `${stats.width}×${stats.height}` : null,
            stats.fps !== null ? `${stats.fps} fps` : null,
            stats.bitrate !== null ? formatBitrate(stats.bitrate) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
      )}
      {zoom.s > 1 && (
        <button className="btn btn-sm tile-zoom-reset" onClick={() => setZoom(ZOOM_IDENTITY)}>
          {t('live.zoom_reset', { z: zoom.s.toFixed(1) })}
        </button>
      )}
      {overlay && (
        <div className="tile-overlay">
          {/* 錯誤代碼只放滑過的提示（給客服），不寫進畫面文字（第十二輪 B12-06） */}
          <span title={err ?? undefined}>{overlay}</span>
          {overlaySub && <span className="small muted tile-reconnect">{overlaySub}</span>}
          {preview && cam.presence !== 'offline' && onPromote && (
            <button className="btn btn-sm" onClick={onPromote}>
              {t('live.go_live')}
            </button>
          )}
          {retryBtn && (
            <button
              className="btn btn-sm"
              data-testid={slowRetry ? 'tile-slow-retry' : autoRetry ? 'tile-reconnect-now' : undefined}
              onClick={() => {
                setAttempt(0);
                stopReasonRef.current = retryStopReason(err, slowRetry);
                setNonce((n) => n + 1);
              }}
            >
              {isRelayStop(err) ? t('live.relay_continue') : autoRetry ? t('live.reconnect_now') : t('common.retry')}
            </button>
          )}
        </div>
      )}
      {relayWarn !== null && !overlay && (
        <div className="tile-relay-warn small" data-testid="relay-warn">
          <span>{t('live.relay_idle_warn', { s: relayWarn })}</span>
          <button
            className="btn btn-sm"
            onClick={() => {
              markActivity();
              setRelayWarn(null);
            }}
          >
            {t('live.relay_continue')}
          </button>
        </div>
      )}
      {multi && lensMsg && !overlay && (
        <div className={`tile-relay-warn small tile-lens-msg${lensMsg.warn ? ' warn' : ''}`} role={lensMsg.warn ? 'alert' : 'status'} data-testid="lens-msg">
          <span>{lensMsg.text}</span>
          {'retry' in lensMsg && lensMsg.retry != null && (
            <button className="btn btn-sm" onClick={() => pickLens(lensMsg.retry!)}>
              {t('common.retry')}
            </button>
          )}
          {'back' in lensMsg && lensMsg.back != null && lensMsg.back !== lensTarget && (
            <button className="btn btn-sm" onClick={() => pickLens(lensMsg.back!)}>
              {t('lens.look', { name: lname(lensMsg.back) })}
            </button>
          )}
        </div>
      )}
      {wantTalk && talkMsg && !overlay && (
        <div className="tile-relay-warn small" role="alert" data-testid="talk-msg">
          <span>{t(`live.talk_${talkMsg}`)}</span>
        </div>
      )}
      {canPtz &&
        ptzOpen &&
        (() => {
          const props = {
            cam,
            lens: multi ? lens : undefined,
            decl: multi ? decl : null,
            // 縮圖固定取 pan_tilt 那顆：畫面不在它上面就先存無縮圖（之後再補，SPEC #multi-lens-ptz）
            captureThumb: () =>
              videoRef.current && firstFrame && (!multi || decl!.panTiltLens === lens) ? captureThumbJpeg(videoRef.current) : Promise.resolve(null),
            onClose: () => setPtzOpen(false),
            onUnsupported: () => {
              setPtzNone(true);
              setPtzOpen(false);
            },
          };
          return cam.parentAid ? <PtzPanel {...props} /> : <DevicePtzPanel {...props} />;
        })()}
      {outOfScope && (
        <span className="tile-scope-badge" data-testid="tile-out-of-scope" title={t('live.out_of_scope_tile', { where: outOfScope })}>
          {outOfScope}
        </span>
      )}
      <div
        className="tile-bar"
        draggable={!!dnd}
        onDragStart={
          dnd
            ? (e) => {
                e.dataTransfer.setData(dnd.dataType, String(dnd.cell));
                e.dataTransfer.effectAllowed = 'move';
              }
            : undefined
        }
      >
        <span className={`dot ${dotState}`} data-testid="tile-dot" data-state={dotState} />
        <span className="tile-name" title={path ?? label} data-testid="tile-name">
          {/* 通道名「lstw-16ch / CH2」：窄格子先縮主機名、通道一定看得到（第三輪第 8 列：截成「lstw-16c…」看不出哪一路） */}
          {label.includes(' / ') ? (
            <>
              <span className="tile-name-host">{label.slice(0, label.lastIndexOf(' / '))}</span>
              <span className="tile-name-ch">{' / ' + label.slice(label.lastIndexOf(' / ') + 3)}</span>
            </>
          ) : (
            label
          )}
        </span>
        <span className="spacer" />
        <div className="tile-actions" data-testid="tile-actions" data-mode={mode}>
        {extraActions}
        {multi && mode !== 'compact' && (
          <LensPicker
            label={t('lens.group')}
            lenses={decl!.lenses.map((l) => ({ id: l.id, name: lname(l.id) }))}
            selected={lensTarget}
            busy={lensPhase?.kind === 'switching' ? lensPhase.to : null}
            failed={lensPhase?.kind === 'failed' ? lensPhase.lens : null}
            disabledReason={lensCaps === null ? t('lens.old_fw') : null}
            ready={!!lensCaps && firstFrame && !err}
            onPick={pickLens}
          />
        )}
        {stream && !preview && mode !== 'compact' && (
          <button
            className={`tile-stream${streamOverride ? ' manual' : ''}${narrow ? ' icon-btn' : ''}`}
            title={t('live.stream_switch', { s: t(`live.stream_${stream}`) })}
            aria-label={t('live.stream_switch', { s: t(`live.stream_${stream}`) })}
            onClick={() => setStreamOverride(stream === 'main' ? 'sub' : 'main')}
          >
            {t(`live.stream_${stream}${narrow ? '_short' : ''}`)}
            {streamOverride && !narrow && ` · ${t('live.stream_manual')}`}
          </button>
        )}
        {stats && !narrow && (
          <button
            className="icon-btn"
            title={t('live.conn_info')}
            aria-label={t('live.conn_info')}
            aria-pressed={statsOpen}
            data-testid="tile-info"
            onClick={() => setStatsOpen((v) => !v)}
          >
            ⓘ
          </button>
        )}
        {wantTalk && firstFrame && !err && !narrow && (
          <>
            <button
              className="icon-btn"
              title={t(listen ? 'live.listen_off' : 'live.listen_on')}
              aria-label={t(listen ? 'live.listen_off' : 'live.listen_on')}
              aria-pressed={listen}
              data-label={t(listen ? 'live.listen_off' : 'live.listen_on')}
              data-testid="tile-listen"
              onClick={() => setListen((v) => !v)}
            >
              {listen ? '🔊' : '🔈'}
            </button>
            <button
              className={`tile-talk${talkState === 'on' ? ' on' : ''}`}
              data-testid="tile-talk"
              data-talk={talkBlock ?? talkState}
              disabled={talkBlock !== null}
              title={t(talkBlock ? `live.talk_${talkBlock}` : 'live.talk_hint')}
              aria-label={t('live.talk')}
              aria-pressed={talkState === 'on' || talkState === 'starting'}
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture?.(e.pointerId);
                talkHold(true);
              }}
              onPointerUp={() => talkHold(false)}
              onPointerCancel={() => talkHold(false)}
              onKeyDown={(e) => {
                if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
                  e.preventDefault();
                  talkHold(true);
                }
              }}
              onKeyUp={(e) => {
                if (e.key === ' ' || e.key === 'Enter') talkHold(false);
              }}
              onBlur={() => talkHold(false)}
            >
              🎤 {t(talkState === 'on' ? 'live.talking' : talkState === 'starting' ? 'live.talk_starting' : 'live.talk')}
            </button>
          </>
        )}
        {firstFrame && !err && canSnapshot && !narrow && (
          <button className="icon-btn" title={t('live.snapshot')} aria-label={t('live.snapshot')} data-label={t('live.snapshot')} onClick={snapshot}>
            ⎙
          </button>
        )}
        {cam.isCamera && !narrow && (
          <button className="icon-btn" title={t('live.replay')} aria-label={t('live.replay')} data-label={t('live.replay')} onClick={replay}>
            ⟲
          </button>
        )}
        {canPtz && !narrow && (
          <button className="icon-btn" title={t('ptz.title')} aria-label={t('ptz.title')} aria-expanded={ptzOpen} onClick={() => setPtzOpen((o) => !o)}>
            ✥
          </button>
        )}
        {onExpand && (
          <button className="icon-btn" title={t('live.expand')} aria-label={t('live.expand')} data-label={t('live.expand')} onClick={onExpand} data-testid="tile-expand">
            ⤢
          </button>
        )}
        {narrow && (
          <TileMenu
            label={t('live.tile_more', { name: label })}
            items={[
              { key: 'stream', hidden: !(stream && !preview && mode === 'compact'), label: t('live.stream_switch', { s: t(`live.stream_${stream ?? 'sub'}`) }), onClick: () => setStreamOverride(stream === 'main' ? 'sub' : 'main') },
              { key: 'info', hidden: !stats, label: t('live.conn_info'), onClick: () => setStatsOpen((v) => !v) },
              { key: 'listen', hidden: !(wantTalk && firstFrame && !err), label: t(listen ? 'live.listen_off' : 'live.listen_on'), onClick: () => setListen((v) => !v) },
              { key: 'snapshot', hidden: !(firstFrame && !err && canSnapshot), label: t('live.snapshot'), onClick: snapshot },
              { key: 'replay', hidden: !cam.isCamera, label: t('live.replay'), onClick: replay },
              { key: 'ptz', hidden: !canPtz, label: t('ptz.title'), onClick: () => setPtzOpen((o) => !o) },
              ...(multi && mode === 'compact' && lensCaps
                ? decl!.lenses
                    .filter((l) => l.id !== lensTarget)
                    .map((l) => ({ key: `lens-${l.id}`, label: t('lens.look', { name: lname(l.id) }), onClick: () => pickLens(l.id) }))
                : []),
            ]}
          />
        )}
        {onClose && (
          <button className="icon-btn" title={t('live.remove')} aria-label={t('live.remove')} onClick={onClose} data-testid="tile-remove">
            ✕
          </button>
        )}
        </div>
      </div>
    </div>
  );
}

/**
 * 鏡頭單選（radiogroup，web 鍵盤：方向鍵選、Tab 停一次；SPEC #multi-lens-a11y）。舊韌體（hello 沒有 caps.lens）看起來停用、滑過說原因；
 * 切換中那顆加轉圈、失敗的那顆紅框。
 */
function LensPicker({
  label,
  lenses,
  selected,
  busy,
  failed,
  disabledReason,
  ready,
  onPick,
}: {
  label: string;
  lenses: { id: number; name: string }[];
  selected: number;
  busy: number | null;
  failed: number | null;
  disabledReason: string | null;
  ready: boolean;
  onPick(id: number): void;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (e: React.KeyboardEvent, i: number) => {
    const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const j = (i + d + lenses.length) % lenses.length;
    refs.current[j]?.focus();
    onPick(lenses[j].id);
  };
  return (
    <div className={`tile-lens${disabledReason ? ' disabled' : ''}`} role="radiogroup" aria-label={label} title={disabledReason ?? undefined} data-testid="tile-lens">
      {lenses.map((l, i) => (
        <button
          key={l.id}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="radio"
          aria-checked={l.id === selected}
          aria-disabled={!!disabledReason || !ready || undefined}
          aria-description={disabledReason ?? undefined}
          tabIndex={l.id === selected ? 0 : -1}
          className={`tile-lens-seg${l.id === selected ? ' on' : ''}${l.id === busy ? ' busy' : ''}${l.id === failed ? ' failed' : ''}`}
          data-lens={l.id}
          onClick={() => !disabledReason && onPick(l.id)}
          onKeyDown={(e) => !disabledReason && onKey(e, i)}
        >
          {l.name}
        </button>
      ))}
    </div>
  );
}

interface TileMenuItem {
  key: string;
  label: string;
  onClick(): void;
  hidden?: boolean;
}

/** 格子的〔⋯〕：選單畫在 body（fixed），不被格子的 overflow 切掉；靠按鈕右緣、往上展開，上面放不下才往下。Esc、點外面關。 */
function TileMenu({ label, items }: { label: string; items: TileMenuItem[] }) {
  const [at, setAt] = useState<{ right: number; top?: number; bottom?: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const shown = items.filter((i) => !i.hidden);
  useEffect(() => {
    if (!at) return;
    const off = (e: MouseEvent) => {
      const n = e.target as Node;
      if (!menu.current?.contains(n) && !btn.current?.contains(n)) setAt(null);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setAt(null);
    const close = () => setAt(null);
    document.addEventListener('mousedown', off);
    document.addEventListener('keydown', esc);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', off);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [at]);
  if (!shown.length) return null;
  const toggle = () => {
    if (at) return setAt(null);
    const r = btn.current!.getBoundingClientRect();
    const right = Math.max(8, window.innerWidth - r.right);
    const need = shown.length * 36 + 12;
    setAt(r.top > need ? { right, bottom: window.innerHeight - r.top + 4 } : { right, top: r.bottom + 4 });
  };
  return (
    <>
      <button ref={btn} type="button" className="icon-btn" title={label} aria-label={label} aria-haspopup="menu" aria-expanded={!!at} onClick={toggle} data-testid="tile-more">
        ⋯
      </button>
      {at &&
        createPortal(
          <div ref={menu} className="ot-menu tile-menu" role="menu" style={{ position: 'fixed', right: at.right, top: at.top ?? 'auto', bottom: at.bottom ?? 'auto' }} data-testid="tile-menu">
            {shown.map((i) => (
              <button key={i.key} type="button" role="menuitem" data-key={i.key} onClick={() => (setAt(null), i.onClick())}>
                {i.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
